import {setTitle} from '@github-ui/document-metadata'
import type {FilterProps} from '@github-ui/filter'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {SidebarLink} from '@github-ui/saved-views'
import {getShortcutsWithSaved, savedViewRouteId, useShortcutDeletion, useShortcutEditor} from '@github-ui/saved-views'
import {RELATES_TO_SEARCH_FEATURE_FLAG} from '@github-ui/shared-filter-providers/constants'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {useOrgAwareProviders} from '@github-ui/shared-filter-providers/providers/use-org-aware-providers'
import {useNavigate} from '@github-ui/use-navigate'
import {useLocation} from '@github-ui/react-router'
import {useEffect, useEffectEvent, useMemo, useState} from 'react'
import {ISSUES_DASHBOARD_PATH, ISSUES_DASHBOARD_SEARCH_PATH} from '../constants/routes'
import {useIssuesDashboardSurfaceStateContext} from '../contexts/IssuesDashboardSurfaceStateContext'
import {deriveSidebarLink} from '@github-ui/shared-dashboard-surface/helpers/layout-helpers'
import {updateCachedIssuesSavedViews} from '../helpers/issues-query-resolver'

import {useAnalytics} from '@github-ui/use-analytics'

export function useIssuesDashboardSurfaceLayoutState() {
  const {sendAnalyticsEvent} = useAnalytics()

  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     The monolith publishes this flag to the issues app payload via `add_client_feature_flag`
     (repo/org/user gated), so it must be read with `useFeatureFlag`, not `isFeatureEnabled`. */
  const relatesToEnabled = useFeatureFlag(RELATES_TO_SEARCH_FEATURE_FLAG)

  const {payload, fetchedQuery, fetchResults, locationSidebarLinkId} = useIssuesDashboardSurfaceStateContext()

  const compactMode = payload.compactMode ?? false
  const sidebarLinks = useMemo(() => payload.sidebarLinks ?? [], [payload.sidebarLinks])

  const location = useLocation()
  const {pathname} = location
  const navigate = useNavigate()

  const [query, setQuery] = useState(fetchedQuery)

  const [savedViews, setSavedViews] = useState(() =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- assertion required for type narrowing; removing it breaks tsc
    payload.savedViews.map(savedView => ({...savedView, searchType: 'issues'}) as SidebarLink),
  )

  const [selectedSidebarLink, setSelectedSidebarLink] = useState<SidebarLink | undefined>(() =>
    deriveSidebarLink({
      sidebarLinkId: locationSidebarLinkId,
      basePath: ISSUES_DASHBOARD_PATH,
      savedViews,
      sidebarLinks,
      location,
    }),
  )

  useEffect(() => {
    const title = selectedSidebarLink ? `${selectedSidebarLink.name} · Issues` : 'Issues'
    const timeoutId = setTimeout(() => setTitle(title), 0)
    return () => clearTimeout(timeoutId)
  }, [selectedSidebarLink])

  const deriveSelectedSidebarLink = useEffectEvent(() =>
    deriveSidebarLink({
      sidebarLinkId: locationSidebarLinkId,
      basePath: ISSUES_DASHBOARD_PATH,
      savedViews,
      sidebarLinks,
      location,
    }),
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery(fetchedQuery)
    setSelectedSidebarLink(deriveSelectedSidebarLink())
    // Recompute on navigation
  }, [fetchedQuery, locationSidebarLinkId, pathname])

  const onShortcutDeleted = (deletedShortcut: SidebarLink) => {
    sendAnalyticsEvent('issues-dashboard.click', 'DELETE_SAVED_VIEW_BUTTON')

    const updated = savedViews.filter(shortcut => shortcut.id !== deletedShortcut.id)
    setSavedViews(updated)
    updateCachedIssuesSavedViews(updated)
    setSelectedSidebarLink(undefined)
  }
  const deleting = useShortcutDeletion(onShortcutDeleted)

  const onShortcutSaved = (savedShortcut: SidebarLink) => {
    const updated = getShortcutsWithSaved(savedViews, savedShortcut)
    setSavedViews(updated)
    updateCachedIssuesSavedViews(updated)

    if (fetchedQuery !== savedShortcut.query) {
      fetchResults({q: savedShortcut.query, page: 1, sidebarLinkId: savedViewRouteId(savedShortcut)})
    } else {
      setSelectedSidebarLink(savedShortcut)
    }
  }
  const editing = useShortcutEditor({type: 'issues', onShortcutSaved})

  function updateQuery(q: string) {
    setQuery(q)
    if (editing.shortcut) {
      editing.setValue('query', q)
    }
  }

  async function kindlyDismissEditing() {
    if (editing.shortcut) {
      const dismissed = await editing.dismiss()
      if (!dismissed) return false
    }
    return true
  }

  async function selectSidebarLink(linkItem: SidebarLink) {
    if (!(await kindlyDismissEditing())) return
    deleting.reset()

    fetchResults({q: linkItem.query, page: 1, sidebarLinkId: savedViewRouteId(linkItem)})
  }

  async function createShortcut() {
    if (!(await kindlyDismissEditing())) return

    sendAnalyticsEvent('issues-dashboard.click', 'CREATE_SAVED_VIEW_BUTTON')

    setSelectedSidebarLink(undefined)
    deleting.reset()

    const defaultQuery = 'is:issue state:open archived:false assignee:@me sort:updated-desc'
    navigate({pathname: ISSUES_DASHBOARD_SEARCH_PATH, search: `?q=${encodeURIComponent(defaultQuery)}`})
    editing.create(defaultQuery, undefined)
    updateQuery(defaultQuery)
  }

  function editShortcut(shortcut: SidebarLink) {
    sendAnalyticsEvent('issues-dashboard.click', 'EDIT_SAVED_VIEW_BUTTON')

    deleting.reset()
    editing.edit(shortcut, query)
  }

  function updateShortcut(shortcut: SidebarLink) {
    sendAnalyticsEvent('issues-dashboard.click', 'UPDATE_SAVED_VIEW_BUTTON')

    deleting.reset()
    editing.updateQuery(shortcut, fetchedQuery)
  }

  const filterProviders = useFilterProviders({
    isIssueSearchScope: true,
    relatesToEnabled,
  })

  const providers = useOrgAwareProviders({
    baseFilterProviders: filterProviders,
    effectiveQuery: query,
    isIssueFieldsGlobalSearchEnabled: false,
  })

  const issuesFilterProps: FilterProps = {
    id: 'issues-dashboard-surface-filter',
    label: 'Search issues',
    variant: 'input',
    placeholder: 'Search issues',
    filterValue: query,
    providers,
    onChange: updateQuery,
    onSubmit: filterQuery => {
      fetchResults({
        q: filterQuery.raw,
        page: 1,
        sidebarLinkId: selectedSidebarLink ? savedViewRouteId(selectedSidebarLink) : '',
      })
    },
    settings: {aliasMatching: true, groupAndKeywordSupport: true},
  }

  return {
    compactMode,
    savedViews,
    selectedSidebarLink,
    sidebarLinks,
    editing,
    deleting,
    methods: {
      selectSidebarLink,
      createShortcut,
      editShortcut,
      updateShortcut,
      fetchResults,
    },
    issuesFilterProps,
  }
}
